import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  BookingSourcesResponseSchema,
  DashboardSummarySchema,
  type BookingSourcesResponse,
  type DashboardSummary,
} from "@/api/types/dashboard";

/** Query-key factory — mirrors useReviews'/useOutreach's convention. */
export const dashboardKeys = {
  all: ["dashboard"] as const,
  summary: () => [...dashboardKeys.all, "summary"] as const,
  bookingSources: () => [...dashboardKeys.all, "booking-sources"] as const,
};

/** The owner home screen's single summary call — GET /v2/dashboard/summary. */
export function useDashboardSummary() {
  return useQuery<DashboardSummary>({
    queryKey: dashboardKeys.summary(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/dashboard/summary");
      return validateResponse(DashboardSummarySchema, data, "GET /v2/dashboard/summary");
    },
    staleTime: 15 * 1000,
  });
}

/**
 * The "does content book events?" instrument — GET /v2/reports/booking-sources.
 * The .csv variant is a plain download link (see bookingSourcesCsvHref below),
 * never fetched as JSON through this hook.
 */
export function useBookingSourcesReport() {
  return useQuery<BookingSourcesResponse>({
    queryKey: dashboardKeys.bookingSources(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/reports/booking-sources");
      return validateResponse(
        BookingSourcesResponseSchema,
        data,
        "GET /v2/reports/booking-sources",
      );
    },
    staleTime: 30 * 1000,
  });
}

/**
 * CSV download hrefs. The API's base path is '/api' (same-origin proxy in
 * dev, same-origin path route in prod per the plan's single-hostname
 * deploy), so a plain anchor with `download` triggers the browser's native
 * download using the httpOnly-cookie auth fallback — these are never
 * fetched as JSON through apiClient/validateResponse.
 */
export const bookingSourcesCsvHref = "/api/v2/reports/booking-sources.csv";
